import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  IconButton, 
  Typography, 
  Box,
  useTheme,
  useMediaQuery,
  Grid,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../assets/images/welcome/logo.webp';
import welcomeImage from '../assets/images/welcome/1.jpg';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    padding: 0,
    maxWidth: '1000px',
    width: '95%',
    background: 'linear-gradient(to bottom right, #FFFFFF, #F8FAFC)',
    overflow: 'hidden',
    [theme.breakpoints.up('md')]: {
      maxHeight: '600px',
      height: '80vh'
    }
  },
  '& .MuiDialogContent-root': {
    padding: 0,
    overflow: 'hidden'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: 0,
  position: 'absolute',
  right: '1rem',
  top: '1rem',
  zIndex: 2,
  '& .closeButton': {
    color: '#475569',
    background: 'rgba(255, 255, 255, 0.9)',
    '&:hover': {
      background: 'rgba(255, 255, 255, 1)',
    }
  },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  marginBottom: '1.5rem',
  '& img': {
    width: 'auto',
    height: '80px',
    objectFit: 'contain',
  }
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: '-50%',
  right: '0',
  bottom: 0,
  overflow: 'hidden',
  '& img': {
    width: 'auto',
    height: '100%',
    objectFit: 'cover',
    minWidth: '100%',
    transform: 'translateX(20%)'
  }
}));

const WelcomeModal = () => {
  const [open, setOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    // Préchargement de l'image
    const img = new Image();
    img.src = welcomeImage;
    img.onload = () => {
      setImageLoaded(true);
      // On attend un peu après le chargement pour un meilleur effet
      setTimeout(() => {
        setOpen(true);
      }, 500);
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  // Ne pas rendre le modal si l'image n'est pas chargée
  if (!imageLoaded) return null;

  return (
    <StyledDialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      TransitionComponent={Zoom}
      TransitionProps={{
        timeout: {
          enter: 500,
          exit: 300
        }
      }}
    >
      <StyledDialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className="closeButton"
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <DialogContent>
        <Grid container sx={{ height: '100%', minHeight: '100%' }}>
          {isDesktop && (
            <Grid item md={6} sx={{ height: '100%', position: 'relative' }}>
              <ImageContainer>
                <img src={welcomeImage} alt="L2MI Welcome" />
              </ImageContainer>
            </Grid>
          )}
          <Grid item xs={12} md={6} sx={{ 
            height: '100%', 
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            background: 'linear-gradient(to right, rgba(255,255,255,0.95), #fff)'
          }}>
            <Box sx={{ 
              p: 4, 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              <LogoContainer>
                <img src={logo} alt="Logo L2MI" />
              </LogoContainer>
              <Typography variant="h4" component="h2" sx={{ 
                color: '#1A365D',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.75rem' },
                mb: 2,
                textAlign: 'left'
              }}>
                Deux entreprises, un seul groupe
              </Typography>
              <Typography 
                sx={{ 
                  color: '#475569',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  textAlign: 'justify'
                }}
              >
                Au sein du groupe, notre priorité est de fidéliser nos clients en leur offrant un service de qualité supérieure qui répond à leurs attentes et exigences spécifiques. Nous nous engageons à mettre en œuvre des actions d'amélioration continue afin de garantir que nos services évoluent avec les besoins de nos clients. En respectant rigoureusement les exigences normatives, nous veillons à ce que chaque interaction avec nos clients soit conforme aux standards les plus élevés de l'industrie.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
    </StyledDialog>
  );
};

export default WelcomeModal;
